// web/src/services/push-subscription.ts
//
// Called once after the customer logs in. Registers the service worker,
// asks the browser for a Push subscription using our VAPID public key,
// and POSTs the subscription to the backend so we can deliver "Free
// Coffee" pushes later. Idempotent: the backend upserts on `endpoint`.

const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type RegisterPushResult =
  | 'subscribed'
  | 'denied'
  | 'unsupported'
  | 'no-vapid'
  | 'no-session';

export async function registerPushSubscription(sessionId: string | null): Promise<RegisterPushResult> {
  if (!sessionId) return 'no-session';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (!VAPID_PUBLIC) return 'no-vapid';

  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    // Use existing permission if granted; only prompt if `default`.
    const existing = Notification.permission;
    const permission = existing === 'granted'
      ? 'granted'
      : existing === 'denied' ? 'denied' : await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // Browser PushManager.subscribe expects a BufferSource backed by a
      // plain ArrayBuffer. Allocate one explicitly to avoid SharedArrayBuffer
      // typing in newer lib.dom.
      const keyBytes = urlBase64ToUint8Array(VAPID_PUBLIC);
      const buf = new ArrayBuffer(keyBytes.byteLength);
      new Uint8Array(buf).set(keyBytes);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: buf,
      });
    }

    const json = sub.toJSON();
    const apiBase = (import.meta.env.VITE_API_BASE_PATH as string | undefined) || '/api';
    await fetch(`${apiBase}/gamification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionId}`,
      },
      body: JSON.stringify({
        action: 'register-push',
        endpoint: json.endpoint,
        p256dh_key: json.keys?.p256dh,
        auth_key: json.keys?.auth,
        user_agent: navigator.userAgent,
      }),
    });
    return 'subscribed';
  } catch (e) {
    console.warn('[push] register failed', e);
    return 'denied';
  }
}
